import {useEffect, useState} from 'react'

import {useSuiContext} from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'

import {NOTIFICATIONS, useNotify} from '../notifications.js'

const useFavorites = ({adId, dealerId, initialIsFavorite, initialIsDealerFavorite}) => {
  const {domain, user} = useSuiContext()
  const notify = useNotify()

  // Every favorite button on the page reads this state, so toggling one keeps the rest in sync
  const [isFavorite, setIsFavorite] = useState(initialIsFavorite)
  const [isDealerFavorite, setIsDealerFavorite] = useState(initialIsDealerFavorite)

  const {USER_NOT_LOGGED_IN_USER_ERROR} = domain.get('config').get('ERRORS')

  // Logging in is not a failure: the login modal takes over and the use case runs again afterwards
  const notifyUnlessUnauthenticated = (error, notification) => {
    if (!(error instanceof USER_NOT_LOGGED_IN_USER_ERROR)) notify(notification)
  }

  useMount(() => {
    // The related ad cards and the discard button execute the same use cases
    const isSameAd = params => params[0]?.adId === adId

    const onAdd = ({params}) => {
      if (!isSameAd(params)) return
      setIsFavorite(true)
    }

    const onAddError = ({params, error}) => {
      if (!isSameAd(params)) return
      notifyUnlessUnauthenticated(error, NOTIFICATIONS.AD_FAVORITE_ADD_ERROR)
    }

    const onDelete = ({params}) => {
      if (!isSameAd(params)) return
      setIsFavorite(false)
    }

    const onDeleteError = ({params, error}) => {
      if (!isSameAd(params)) return
      notifyUnlessUnauthenticated(error, NOTIFICATIONS.AD_FAVORITE_DELETE_ERROR)
    }

    const addAdFavorite$ = domain.get('add_user_favorites_use_case').$.execute.subscribe(onAdd, onAddError)
    const deleteAdFavorite$ = domain.get('delete_user_favorites_use_case').$.execute.subscribe(onDelete, onDeleteError)

    return () => {
      addAdFavorite$.dispose()
      deleteAdFavorite$.dispose()
    }
  })

  useMount(() => {
    // Dealer favorites are not streamified, so the error arrives inlined in the result tuple
    const onDealerFavoriteResult =
      ({nextIsDealerFavorite, success, failure}) =>
      ({result: [error]}) => {
        if (error) return notifyUnlessUnauthenticated(error, failure)

        setIsDealerFavorite(nextIsDealerFavorite)
        notify(success)
      }

    const addDealerFavorite$ = domain.get('add_user_favorites_dealer_use_case').subscribe(
      onDealerFavoriteResult({
        nextIsDealerFavorite: true,
        success: NOTIFICATIONS.DEALER_FAVORITE_ADDED,
        failure: NOTIFICATIONS.DEALER_FAVORITE_ADD_ERROR
      })
    )

    const deleteDealerFavorite$ = domain.get('delete_user_favorites_dealer_use_case').subscribe(
      onDealerFavoriteResult({
        nextIsDealerFavorite: false,
        success: NOTIFICATIONS.DEALER_FAVORITE_DELETED,
        failure: NOTIFICATIONS.DEALER_FAVORITE_DELETE_ERROR
      })
    )

    return () => {
      addDealerFavorite$.unsubscribe()
      deleteDealerFavorite$.unsubscribe()
    }
  })

  // Restoring from the back/forward cache brings this page's state back untouched, so a favorite
  // toggled somewhere else in between would still render as it was when the page was frozen
  useEffect(() => {
    if (!user) return

    const revalidate = async ({persisted}) => {
      if (!persisted) return

      try {
        const {ads = [], contracts = []} = await domain.get('get_user_favorites_use_case').execute()

        setIsFavorite(ads.some(({id}) => Number(id) === Number(adId)))
        setIsDealerFavorite(contracts.some(({id}) => Number(id) === Number(dealerId)))
      } catch (e) {}
    }

    window.addEventListener('pageshow', revalidate)

    return () => window.removeEventListener('pageshow', revalidate)
  }, [adId, dealerId, domain, user])

  return {isFavorite, isDealerFavorite}
}

export default useFavorites
